import React, { useEffect } from 'react'
import { Box } from '@mui/material'
import {
  Window,
  MessageList,
  MessageInput,
  useMessageInputContext,
  useChannelStateContext,
  MessageInputFlat,
  useMessageContext,
  MessageSimple,
  useChannelActionContext,
  Attachment,
  Card,
} from 'stream-chat-react'
import ChannelHeader from './channel-header'
import useQueryString from 'lib/hooks/use-query-string'
import { useMediaInfoFactory } from 'lib/hooks/use-media-info-factory'
import { useVideoAnalysis } from 'lib/hooks/use-video-analysis'
import {
  GroupEventAttachmentCard,
  LessonTypeAttachmentCard,
} from './components'
import {
  OUR_TYPES,
  prepareCustomAttachment,
  replaceMentions,
  fileUploadRequestOverride,
} from './utils'
import { useAttachmentContext } from './use-attachment-context'
import PlaceholderWithQrCode from './components/placeholder-with-qr-code'
import { useAuth } from 'lib/auth'

const UpdatedCard = props => {
  switch (props?.type) {
    case 'pga_group_event':
      return <GroupEventAttachmentCard {...props} />
    case 'pga_lesson_type':
      return <LessonTypeAttachmentCard {...props} />
    case 'pga_student_library_upload':
      // This is a stopgap effort. In the future, we may instead support displaying the video.
      return <PlaceholderWithQrCode />
    default:
      return <Card {...props} />
  }
}

/* Note that Stream for web encapsulates all non-standard attachment types as a Card. */
export const CustomAttachment = props => {
  return <Attachment {...props} Card={UpdatedCard} />
}

export const CustomMessage = () => {
  let { message } = useMessageContext()
  const { user } = useAuth()
  const mediaInfoFactory = useMediaInfoFactory()
  const { attachments } = message
  const isMyMessage = message.user?.id === user?.externalId
  const nonCompliantVideos = useVideoAnalysis(
    attachments,
    mediaInfoFactory,
    !isMyMessage,
  )
  let customAttachment = false
  let hasUrlAttachment = false

  // Listed Group Events may be scraped and generate a URL preview. This causes TWO preview cards to appear.
  // This checks for the presence of our custom attachment and an URL attachment and prioritizes ours.
  attachments.length > 0 &&
    attachments.forEach(att => {
      if (OUR_TYPES.includes(att.type)) customAttachment = att
      if (att.og_scrape_url) hasUrlAttachment = true
    })

  if (customAttachment && hasUrlAttachment) {
    message.attachments = [customAttachment]
  }

  if (message.mentioned_users.length > 0) {
    message = replaceMentions({ message: message })
  }

  return (
    <>
      {nonCompliantVideos.length > 0 && (
        <Box maxWidth="375px">
          <PlaceholderWithQrCode type="unsupported-video" />
        </Box>
      )}
      <MessageSimple message={message} />
    </>
  )
}

const UserMessageInput = () => {
  const { setText } = useMessageInputContext()
  const { shared } = useQueryString()
  const prompt = 'Check out this link for booking info: '

  useEffect(() => {
    if (shared) {
      setText(prompt + shared)
    }
  }, [shared, setText])

  return (
    <Box
      sx={{
        position: 'sticky',
        bottom: 0,
        zIndex: 1,
      }}
    >
      <MessageInputFlat />
    </Box>
  )
}

export const ChannelInner = () => {
  const { sendMessage } = useChannelActionContext()
  const { shared, attType, id } = useAttachmentContext()
  const { channel } = useChannelStateContext()

  const handleFileUploadRequest = async file => {
    return fileUploadRequestOverride(file, channel)
  }

  const overrideSubmitHandler = message => {
    // This handler sends the message as normal, but first checks if we are/aren't
    // sending one of our custom attachments.

    const customAttachment = prepareCustomAttachment({
      message: message,
      shared: shared,
      attType: attType,
      id: id,
    })
    if (customAttachment) {
      message.attachments.push(customAttachment)
    }

    const updatedMessage = {
      attachments: message.attachments,
      mentioned_users: message.mentioned_users,
      parent_id: message.parent?.id,
      parent: message.parent,
      text: message.text,
    }

    if (sendMessage) {
      sendMessage(updatedMessage)
    }
  }

  return (
    <Window>
      <ChannelHeader />
      <MessageList style={{ textAlign: 'left !important' }} />
      <MessageInput
        focus={false}
        Input={UserMessageInput}
        doFileUploadRequest={handleFileUploadRequest}
        overrideSubmitHandler={overrideSubmitHandler}
      />
    </Window>
  )
}
